import * as Sentry from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Fingerprint, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { useGetUserInfo } from '@/common/hooks/useGetUserInfo'
import {
  ApplicationAccountUserRole,
  ApplicationAccountUserStatus,
} from '@/common/models/applicationAccountUser'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { NewApplicationAccountUserForm } from '../schemas/NewApplicationAccountUserSchema'
import { useSelectedUserApplicationAccountUserStore } from '../stores/useSelectedUserApplicationAccountUserStore'
import { useManageApplicationAccountUser } from './useManageApplicationAccountUser'

export type PostApplicationAccountUserProps = {
  applicationId: string
  isIngress: boolean
  role: ApplicationAccountUserRole
  startDate: string
  status: ApplicationAccountUserStatus
  userId: string
  accountId?: string
  endDate?: string
  externalUserId?: string
  metadata?: Record<string, unknown>
}

async function postApplicationAccountUser(
  data: PostApplicationAccountUserProps,
) {
  return api.backend.post('/application-account-users', data)
}

export function useNewApplicationAccountUser() {
  const { t } = useTranslation('sso')
  const { userAccess } = useSelectedUserApplicationAccountUserStore()
  const { userApplications } = useManageApplicationAccountUser()
  const { userInfo } = useGetUserInfo()

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    PostApplicationAccountUserProps
  >({
    mutationFn: postApplicationAccountUser,
    onSuccess: (_, variables) => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: Fingerprint,
          title: t(
            'access.use_new_application_account_user.toast_success.title',
          ),
        },
      })
      queryClient.invalidateQueries({
        queryKey: ['/users-access/paginate'],
        exact: false,
      })

      if (variables.userId === userInfo?.id) {
        queryClient.invalidateQueries({ queryKey: ['/user/get-user-info'] })
        queryClient.invalidateQueries({
          queryKey: ['/user/current-accounts'],
          exact: false,
        })
      }
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('access.use_new_application_account_user.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t(
              'access.use_new_application_account_user.toast_error.description',
            ),
        },
      })
    },
  })

  const handleCreateNewApplicationAccountUser = async (
    applicationId: string,
    data: NewApplicationAccountUserForm,
  ) => {
    if (!userAccess) {
      Sentry.captureMessage('User access is not selected', {
        level: 'error',
        extra: { applicationId, data },
      })
      throw new Error('User access is not selected')
    }

    if (!userApplications) {
      Sentry.captureMessage('User applications are not loaded', {
        level: 'error',
        extra: { applicationId, data },
      })
      throw new Error('User applications are not loaded')
    }

    const newApplicationAccountUser = data.accesses.find(
      (access) => access.__isNew,
    )

    if (!newApplicationAccountUser) {
      Sentry.captureMessage('No new application account user found', {
        level: 'error',
        extra: { applicationId, data },
      })
      throw new Error('No new application account user found')
    }

    const application = userApplications.find(
      (app) => app.application.id === applicationId,
    )

    if (!application) {
      Sentry.captureMessage('Application not found', {
        level: 'error',
        extra: { applicationId, data },
      })
      throw new Error('Application not found')
    }

    const payload: PostApplicationAccountUserProps = {
      applicationId,
      userId: userAccess.id,
      accountId: newApplicationAccountUser.accountId,
      role: newApplicationAccountUser.role,
      isIngress: application.access.length === 0,
      startDate: new Date().toISOString(),
      status: ApplicationAccountUserStatus.CONNECT,
    }

    return mutateAsync(payload)
  }

  return {
    handleCreateNewApplicationAccountUser,
    isCreatingNewApplicationAccountUser: isPending,
  }
}
